import { Box, Divider, Stack, Typography } from "@mui/material";
import InternAppReviewHistoryTable from "./InternAppReviewHistoryTable";
import InternScheduledBuildsTable from "./InternScheduledBuildsTable";

interface PendingChange {
  field: string;
  new_value?: string;
  changed_at: string;
  requires_build?: boolean;
}

interface ScheduledBuildRow {
  id: string;
  platform: string;
  pendingChanges: PendingChange[];
  firstChangeAt: string;
  lastChangeAt: string;
  scheduledFor: string;
  status: string;
  changesCount: number;
  pipelineId?: string | null;
}

interface ReviewRow {
  id: string;
  platform: string;
  appVersion: string;
  buildNumber: string;
  status: string;
  statusDisplay: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  rejectionReason: string;
}

interface Props {
  builds: ScheduledBuildRow[];
  reviews: ReviewRow[];
}

const HISTORY_TABLE_HEIGHT = 320;

/**
 * Single surface for schedule + review history so paired tables
 * share one height and scroll independently.
 */
const InternBuildHistorySection = ({ builds, reviews }: Props) => {
  return (
    <Box
      sx={{
        bgcolor: "var(--intern-bg-primary)",
        border: "1px solid var(--intern-border-default)",
        borderRadius: 2,
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid var(--intern-border-default)",
        }}
      >
        <Typography
          variant="subtitle1"
          fontWeight={600}
          sx={{ color: "var(--intern-text-primary)", fontSize: "1rem" }}
        >
          History
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: "var(--intern-text-tertiary)",
            fontSize: "0.75rem",
            mt: 0.25,
          }}
        >
          Past scheduled builds and store review submissions
        </Typography>
      </Box>

      <Stack
        direction={{ xs: "column", lg: "row" }}
        divider={
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "var(--intern-border-default)",
              display: { xs: "none", lg: "block" },
            }}
          />
        }
        sx={{ alignItems: "stretch" }}
      >
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              px: 2,
              pt: 1.5,
              pb: 1,
            }}
          >
            Scheduled Builds
          </Typography>
          <Box sx={{ flex: 1, minHeight: HISTORY_TABLE_HEIGHT }}>
            <InternScheduledBuildsTable
              builds={builds}
              embedded
              tableMaxHeight={HISTORY_TABLE_HEIGHT}
            />
          </Box>
        </Box>

        <Divider
          sx={{
            borderColor: "var(--intern-border-default)",
            display: { xs: "block", lg: "none" },
          }}
        />

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Typography
            variant="caption"
            sx={{
              color: "var(--intern-text-tertiary)",
              fontSize: "0.6875rem",
              fontWeight: 700,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              px: 2,
              pt: 1.5,
              pb: 1,
            }}
          >
            App Reviews
          </Typography>
          <Box sx={{ flex: 1, minHeight: HISTORY_TABLE_HEIGHT }}>
            <InternAppReviewHistoryTable
              reviews={reviews}
              embedded
              tableMaxHeight={HISTORY_TABLE_HEIGHT}
            />
          </Box>
        </Box>
      </Stack>
    </Box>
  );
};

export default InternBuildHistorySection;
